import { useMutation } from '@tanstack/react-query';
import { getBFFApiClient } from '@acp/feature/auth';
import { useToast } from '@chakra-ui/react';
import { Invoice } from '@acp/shared';

export function useDownloadInvoicePdf({
    onSuccess = () => {},
    onError = () => {},
}: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
} = {}) {
    const toast = useToast();

    const mutation = useMutation({
        mutationFn: async ({
            invoice,
        }: {
            invoice: Invoice;
        }) => {
            toast({
                title: 'We\'re preparing your invoice',
                description: 'Please wait while we prepare your invoice for download.',
                status: 'info',
            });
            const res = await getBFFApiClient().get<Blob>(invoice.pdfUrl, {
                responseType: 'blob',
            });

            if (res.status !== 200) {
                throw new Error('Failed to download invoice PDF');
            }

            return URL.createObjectURL(res.data);
        },
        onSuccess: (url, { invoice }) => {
            // We are breaking React's rules here by directly manipulating the DOM
            // it is OK, so long as we clean up after ourselves
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice-${invoice.invoiceNumber}.pdf`;
            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast({
                title: `Invoice #${invoice.invoiceNumber} downloaded`,
                description: 'Please check your downloads folder for the invoice.',
                status: 'success',
                isClosable: true,
            });

            onSuccess();
        },
        onError: (error, { invoice }) => {
            toast({
                title: `Invoice #${invoice.invoiceNumber} failed to download`,
                description: 'We experienced an error while trying to download the invoice. Please try again.',
                status: 'error',
                isClosable: true,
            });
            onError(error);
        },
    });

    return {
        isDownloading: mutation.isPending,
        download: mutation.mutate,
    };
}
